import * as React from 'react';
import { StyleSheet } from 'aphrodite/no-important';
import { TYPOGRAPHY_SIZE, TypographySize } from 'mm-theme-configuration/dist/consts';
import { MEDIA_BREAKPOINTS } from '../../../../../mediaQueries.const';
import { TextLinkComponent, generateLinkStyleFromTheme } from '../../../../partials/link/TextLink';
import { Span, semanticCSS } from '../../../../../typography/semanticTags';
import { notActiveLinkIdleColor } from '../header.utils';
import { BaseUnit, baseUnit } from '../../../../../theming/baseUnitDefinition';
import { TranslatedFontSize } from '../../../../../theming/fontSizeTranslator';
import { getStyleBySize } from '../../../../../typography/semanticTags/semanticTags.utils';
import { useTheme } from '../../../../../theming/useTheme';
import { NavigationLink } from '../primaryHeader/PrimaryHeader';

const componentConfigThemeKey = 'secondaryLink';

interface SecondaryLinkDataProps {
  linksLength: number;
  index: number;
  isHoveredLink: boolean;
  pagePath: string;
  linkData: NavigationLink;
  maxVisibleLinks: number;
}

interface SecondaryLinkThemeProps {
  baseUnit: BaseUnit;
  hoverOtherLinksColors: string;
  idleColor: string;
  activeStateColor: string;
  hoverStateColor: string;
  fontSizesStyles: TranslatedFontSize;
  fontSize: TypographySize;
}

const getTextStyle = (isLastLink: boolean) => {
  return StyleSheet.create({
    link: {
      width: 'max-content',
      [MEDIA_BREAKPOINTS.medium]: {
        paddingRight: isLastLink ? `${baseUnit * 2}px` : 0,
      },
      [MEDIA_BREAKPOINTS.small]: {
        paddingRight: isLastLink ? `${baseUnit * 2}px` : 0,
      },
    },
  });
};

export const isLastLink = (index: number, linksLength: number) => index + 1 === linksLength;

const getContentStyle = (isActiveLink: boolean, fontSizesStyles: TranslatedFontSize, fontSize: TypographySize) => {
  const fontSizesActive = { fontSizeSmall: TYPOGRAPHY_SIZE.BIGGER, fontSizeMedium: TYPOGRAPHY_SIZE.BIGGER, fontSizeLarge: TYPOGRAPHY_SIZE.BIGGER };
  const fontSizesNotActive = { fontSizeSmall: fontSize, fontSizeMedium: fontSize, fontSizeLarge: fontSize };
  return isActiveLink ? getStyleBySize(fontSizesStyles, fontSizesActive) : getStyleBySize(fontSizesStyles, fontSizesNotActive);
};

export const SecondaryLink: React.FunctionComponent<SecondaryLinkDataProps> = props => {
  const { linksLength, index, isHoveredLink, pagePath, linkData, maxVisibleLinks = 6 } = props;
  const { activeStateColor, hoverOtherLinksColors, idleColor, hoverStateColor, fontSizesStyles, fontSize } = useTheme<SecondaryLinkThemeProps>(componentConfigThemeKey);
  const isActiveLink = linkData.link.href === pagePath;
  const calculatedIdleColor = isActiveLink ? activeStateColor : notActiveLinkIdleColor(isHoveredLink, hoverOtherLinksColors, idleColor);
  const calculatedHoverColor = isActiveLink ? activeStateColor : hoverStateColor;

  const linkStyleBasedOnScreen = linksLength > maxVisibleLinks && index >= maxVisibleLinks - 1
    ? {
      [MEDIA_BREAKPOINTS.large]: {
        display: 'none',
      },
      [MEDIA_BREAKPOINTS.small]: {
        display: 'inline-flex',
      },
      [MEDIA_BREAKPOINTS.medium]: {
        display: 'inline-flex',
      },
    }
    : {};

  const linkStyle = generateLinkStyleFromTheme({ idleColor: calculatedIdleColor, activeStateColor, hoverStateColor: calculatedHoverColor }, linkStyleBasedOnScreen);
  const style = getTextStyle(isLastLink(index, linksLength));
  const contentStyle = getContentStyle(isActiveLink, fontSizesStyles, fontSize);

  return (
    <TextLinkComponent href={linkData.link.href} linkStyle={linkStyle}>
      <Span styles={semanticCSS(contentStyle, style.link)}>{linkData.link.text}</Span>
    </TextLinkComponent>
  );
};
